import React from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { PropertyTypeOption } from "@/lib/types/propertyTypes";

interface PropertyTypeSelectorProps {
  propertyTypes: PropertyTypeOption[];
  loading: boolean;
  propertyType: string;
  onPropertyTypeChange: (value: string) => void;
  className?: string; // For the outer div
  triggerClassName?: string; // Specifically for the SelectTrigger
}

const PropertyTypeSelector = ({
  propertyTypes,
  loading,
  propertyType,
  onPropertyTypeChange,
  className, // Consumed by the outer div
  triggerClassName, // Consumed by SelectTrigger
}: PropertyTypeSelectorProps) => (
  // The outer div's classes can be controlled by the `className` prop if needed,
  // but the original classes are specific to its old layout.
  // For the new SearchForm, the wrapping div there controls width.
  // So, we might not need to pass `className` to this component's outer div from new SearchForm.
  // Let's keep the original outer div classes for now, or make it very basic.
  // The new SearchForm wraps this in a <div className="w-full md:w-1/4">.
  // The `border-b md:border-b-0 md:border-r border-gray-200` is from old layout.
  // For the new SearchForm, this component is just one of the items in a flex row.
  // It should not have its own borders that interfere with the main form's border.
  <div className={className || "w-full"}>
    {" "}
    {/* Simplified outer div, width controlled by parent */}
    {loading ? (
      // Use triggerClassName for skeleton height to match, or a fixed height
      <Skeleton
        className={
          triggerClassName
            ? triggerClassName.split(" ").find((c) => c.startsWith("h-")) ||
              "h-12"
            : "h-12" + " w-full"
        }
      />
    ) : (
      <Select value={propertyType} onValueChange={onPropertyTypeChange}>
        <SelectTrigger
          className={
            triggerClassName ||
            "h-16 px-4 w-full text-left border-gray-300 rounded-md focus:ring-0"
          }
        >
          <SelectValue placeholder="All Property Types" />
        </SelectTrigger>
        <SelectContent className="bg-white max-h-[300px] z-50">
          <SelectItem value="all">All Property Types</SelectItem>
          {Array.isArray(propertyTypes) &&
            propertyTypes.map((type) => (
              <SelectItem
                key={type.value || "type-default"}
                value={type.value || "unknown-type"}
                className="cursor-pointer"
              >
                {type.label || "Unknown Type"}
              </SelectItem>
            ))}
        </SelectContent>
      </Select>
    )}
  </div>
);

export default PropertyTypeSelector;
